import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../utils/constants";

export default function AdminUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });

  // İzin isimleri (çok dilli olması için t() ile çevrilebilir hale getirilebilir)
  const permissionNames = {
    1: t("permissions.analytics"),
    2: t("permissions.products"),
    3: t("permissions.productsCreate"),
    4: t("permissions.productsUpdate"),
    5: t("permissions.productsDelete"),
    6: t("permissions.orders"),
    7: t("permissions.brands"),
    8: t("permissions.brandsCreate"),
    9: t("permissions.brandsUpdate"),
    10: t("permissions.brandsDelete"),
    11: t("permissions.categories"),
    12: t("permissions.categoriesCreate"),
    13: t("permissions.categoriesUpdate"),
    14: t("permissions.categoriesDelete"),
    15: t("permissions.banners"),
    16: t("permissions.bannersCreate"),
    17: t("permissions.bannersUpdate"),
    18: t("permissions.bannersDelete"),
    19: t("permissions.locations"),
    20: t("permissions.locationsCreate"),
    21: t("permissions.locationsUpdate"),
    22: t("permissions.locationsDelete"),
    23: t("permissions.locationPrices"),
    24: t("permissions.locationPricesCreate"),
    25: t("permissions.locationPricesUpdate"),
    26: t("permissions.locationPricesDelete"),
    27: t("permissions.orderTimes"),
    28: t("permissions.orderTimesCreate"),
    29: t("permissions.orderTimesUpdate"),
    30: t("permissions.orderTimesDelete"),
    31: t("permissions.users"),
    32: t("permissions.usersCreate"),
    33: t("permissions.usersUpdate"),
    34: t("permissions.usersDelete"),
    35: t("permissions.settings"),
  };

  // Sadece ana izinleri göster (detaylı CRUD izinlerini gizliyoruz)
  const mainPermissions = [1, 2, 6, 7, 11, 15, 19, 23, 27, 31, 35];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userCookie = Cookies.get("user");
        if (!userCookie) return navigate("/admin");

        const user = JSON.parse(userCookie);
        if (!user?.id) return navigate("/admin");

        const { data: { permissions } } = await axios.get(
          `${CONSTANTS.baseRoute}/permissions`,
          { params: { user_id: user.id } }
        );
        setUserPermissions(permissions);

        if (!permissions.includes(31)) {
          setMessage({ type: "danger", text: t("users.noPermissionView") });
          setTimeout(() => navigate("/admin/home"), 2500);
          return;
        }

        const { data: usersData } = await axios.get(`${CONSTANTS.baseRoute}/users`);
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
        setMessage({
          type: "danger",
          text: error.response?.data?.error || t("users.fetchError"),
        });
        if ([401, 403].includes(error.response?.status)) navigate("/admin");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, t]);

  const hasPermission = (id) => userPermissions.includes(id);

  const showNotification = (type, text, duration = 3500) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), duration);
  };

  const handleDelete = async (userId) => {
    if (!hasPermission(34)) return showNotification("danger", t("users.noPermissionDelete"));

    if (!window.confirm(t("users.confirmDelete"))) return;

    try {
      await axios.delete(`${CONSTANTS.baseRoute}/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      showNotification("success", t("users.userDeletedSuccess"));
    } catch (error) {
      showNotification("danger", error.response?.data?.error || t("users.deleteError"));
    }
  };

  const handleEdit = (userId) => {
    if (!hasPermission(33)) return showNotification("danger", t("users.noPermissionEdit"));
    navigate(`/admin/home/users/edit/${userId}`);
  };

  if (loading) {
    return (
      <div className="admin-main p-4 d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-main ps-2 pe-3 py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex gap-2 align-items-center">
          <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn admin-icon-btn" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h4 className="admin-page-title">{t("users.title")}</h4>
        </div>

        {hasPermission(32) && (
          <button
            className="btn btn-outline-primary d-flex align-items-center gap-2 shadow-sm"
            onClick={() => navigate("/admin/home/users/add")}
          >
            <i className="bi bi-plus-circle"></i>
            {t("users.addUser")}
          </button>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div
          className={`alert alert-${message.type} shadow-sm mb-4 alert-dismissible fade show`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage({ type: "", text: "" })}
          ></button>
        </div>
      )}

      <div className="admin-card overflow-hidden">
        <div
          className="admin-card-header d-flex justify-content-between align-items-center"
        >
          <span><i className="bi bi-people-fill me-2"></i>{t("users.usersList")}</span>
          <span className="badge bg-light text-dark fs-6">{users.length}</span>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0 text-center align-middle">
              <thead className="table-light">
                <tr>
                  <th>{t("users.name")}</th>
                  <th>{t("users.phone")}</th>
                  <th>{t("users.role")}</th>
                  <th>{t("users.permissions")}</th>
                  <th className="text-end pe-4">{t("users.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td className="fw-medium">{user.name || "—"}</td>
                      <td>{user.phone || "—"}</td>
                      <td className="text-capitalize">{user.role || "user"}</td>
                      <td className="text-start">
                        {user.permissions?.length > 0 ? (
                          user.permissions
                            .filter(p => mainPermissions.includes(p))
                            .map(p => permissionNames[p] || `ID: ${p}`)
                            .join(", ")
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-end pe-4">
                        {hasPermission(33) && (
                          <button
                            className="btn btn-sm btn-outline-primary me-2 shadow-sm"
                            onClick={() => handleEdit(user.id)}
                            title={t("common.edit")}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                        )}
                        {hasPermission(34) && (
                          <button
                            className="btn btn-sm btn-outline-danger shadow-sm"
                            onClick={() => handleDelete(user.id)}
                            title={t("common.delete")}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-5 text-muted">
                      <i className="bi bi-person-slash display-4 mb-3 d-block text-muted"></i>
                      {t("users.noUsersFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}